import moment from 'moment';
import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled, { ThemeContext } from 'styled-components';

import { Button, Input } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';

const PillLabel = styled.label<{ $color: string }>`
	font-size: 14px;
	color: #ffffff;
	background-color: ${(props) => props.$color};
	border-radius: 3px;

	padding: 2px 6px;
	display: block;
	width: 70px;
	text-align: center;
`;

function ConnectorLogLabel({ logLevel }: { logLevel: graphql.ConnectorLogLevel }) {
	const theme = useContext(ThemeContext);

	const { colors } = theme;

	if (logLevel === graphql.ConnectorLogLevel.Info) {
		return <PillLabel $color={colors.blue}>Info</PillLabel>;
	}

	if (logLevel === graphql.ConnectorLogLevel.Warning) {
		return <PillLabel $color={colors.yellow}>Warning</PillLabel>;
	}

	return <PillLabel $color={colors.red}>Error</PillLabel>;
}

const ConnectorLogListItem = styled(Flex)``;

const LoadMoreItems = styled.button`
	background: none;
`;

const NoMoreItems = styled(Flex)``;

const ConnectorLogList = styled(Flex)`
	${ConnectorLogListItem}:nth-child(odd) {
		background-color: ${(props) => props.theme.colors.whiteBlue};
	}

	${NoMoreItems} {
		display: block;
	}

	${LoadMoreItems}, ${NoMoreItems} {
		background-color: ${(props) => props.theme.colors.whiteBlue};
		flex: 1;
		border-top: 1px solid ${(props) => props.theme.colors.lightBlueDark};
		width: 100%;
		padding: 18px 8px;
		text-align: center;
	}
`;

export default function ConnectorLogsContainer() {
	const resultsPerPage = 100;

	const [pagination, setPagination] = useState<{ first?: number; after?: string }>({
		first: resultsPerPage,
	});
	const [logDate, setLogDate] = useState<string>(moment().format('YYYY-MM-DD'));
	const [minimumLogLevel, setMinimumLogLevel] = useState<graphql.ConnectorLogLevel>(
		graphql.ConnectorLogLevel.Info,
	);
	const [items, setItems] = useState<graphql.ConnectorLog[]>([]);
	const [endCursor, setEndCursor] = useState<string | undefined>();
	const [hasNextPage, setHasNextPage] = useState<boolean>(false);

	const logsEndRef = useRef<HTMLDivElement | null>(null);

	const { data, loading } = graphql.useConnectorLogsQuery({
		variables: {
			date: logDate,
			minimumLogLevel,
			...pagination,
		},
		fetchPolicy: 'network-only',
	});

	const loadMoreItems = () => {
		setPagination({ first: resultsPerPage, after: endCursor });
	};

	const changeFilters = (date: string, logLevel: graphql.ConnectorLogLevel) => {
		setItems([]);
		setEndCursor(undefined);
		setHasNextPage(false);
		setLogDate(date);
		setMinimumLogLevel(logLevel);
		setPagination({
			first: resultsPerPage,
		});
	};

	const changeDay = (offset: number) => {
		changeFilters(
			moment(logDate, 'YYYY-MM-DD').add(offset, 'days').format('YYYY-MM-DD'),
			minimumLogLevel,
		);
	};

	useEffect(() => {
		if (data) {
			setItems(items.concat(data.connectorLogs.edges.map(({ node }) => node)));
			setEndCursor(data.connectorLogs.pageInfo.endCursor ?? undefined);
			setHasNextPage(data.connectorLogs.pageInfo.hasNextPage);
			setTimeout(() => {
				logsEndRef?.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
			}, 100);
		}
	}, [data]);

	return (
		<Box mx={3}>
			<Box mb={3}>
				<SectionTitle>Connector logs</SectionTitle>
			</Box>

			<Flex flexDirection="row" justifyContent="center" alignItems="center" mb={3}>
				<Button
					small
					onClick={() => changeDay(-1)}
					disabled={loading}
					title="Previous day"
					type="transparent"
				/>
				<Box mx={2}>
					<Input.DatePicker
						value={moment(logDate, 'YYYY-MM-DD').toDate()}
						onChange={(startDate) => {
							changeFilters(moment(startDate).format('YYYY-MM-DD'), minimumLogLevel);
						}}
						disabled={loading}
					/>
				</Box>
				<Button
					onClick={() => changeDay(1)}
					disabled={loading || moment(logDate).isAfter(moment().subtract(1, 'day').endOf('day'))}
					title="Next day"
					small
					type="transparent"
				/>
			</Flex>

			<ConnectorLogList flexDirection="column">
				{!loading && items.length === 0 && (
					<ConnectorLogListItem p={2}>No items to show for this date</ConnectorLogListItem>
				)}
				{items.map((node) => (
					<ConnectorLogListItem key={node.id} flexDirection="row" alignItems="center" py={1}>
						<Box width={1 / 12} ml={2}>
							{moment(node.timestamp).format('HH:mm:ss')}
						</Box>
						<Box width={1 / 12}>
							<ConnectorLogLabel logLevel={node.logLevel} />
						</Box>
						<Box width={10 / 12} mr={2}>
							{node.message}
						</Box>
					</ConnectorLogListItem>
				))}
				{!loading && hasNextPage && (
					<LoadMoreItems onClick={loadMoreItems}>Click here to load more items</LoadMoreItems>
				)}
				{!loading && !hasNextPage && <NoMoreItems>No more items</NoMoreItems>}
			</ConnectorLogList>
			<div ref={logsEndRef} />
		</Box>
	);
}
